// src/components/billing/BillingOverview.js
import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "../ui/Card";
import { Button } from "../ui/Button";
import {
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";

const BillingOverview = ({ overview, onRefresh }) => {
  const {
    monthly_revenue = 0,
    previous_month_revenue = 0,
    total_billable_players = 0,
    previous_month_players = 0,
    average_revenue_per_club = 0,
    total_active_clubs = 0,
    growth_rate = 0,
    churn_rate = 0,
    annual_recurring_revenue = 0,
  } = overview;

  const revenueChange = monthly_revenue - previous_month_revenue;
  const revenueChangePercent =
    previous_month_revenue > 0
      ? ((revenueChange / previous_month_revenue) * 100).toFixed(1)
      : 0;

  const playerChange = total_billable_players - previous_month_players;
  const playerChangePercent =
    previous_month_players > 0
      ? ((playerChange / previous_month_players) * 100).toFixed(1)
      : 0;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const MetricCard = ({
    title,
    value,
    change,
    changePercent,
    icon: Icon,
    color,
  }) => (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Icon className={`h-8 w-8 ${color}`} />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{title}</p>
              <p className="text-2xl font-bold text-gray-900">{value}</p>
            </div>
          </div>
          {change !== undefined && (
            <div className="text-right">
              <div
                className={`flex items-center ${
                  change >= 0 ? "text-green-600" : "text-red-600"
                }`}
              >
                {change >= 0 ? (
                  <ArrowTrendingUpIcon className="h-4 w-4 mr-1" />
                ) : (
                  <ArrowTrendingDownIcon className="h-4 w-4 mr-1" />
                )}
                <span className="text-sm font-medium">{changePercent}%</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">vs last month</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Revenue Overview
          </h2>
          <p className="text-sm text-gray-600">
            Current month performance and trends
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <ArrowPathIcon className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Main Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Monthly Revenue"
          value={formatCurrency(monthly_revenue)}
          change={revenueChange}
          changePercent={revenueChangePercent}
          icon={CurrencyDollarIcon}
          color="text-green-600"
        />

        <MetricCard
          title="Billable Players"
          value={total_billable_players.toLocaleString()}
          change={playerChange}
          changePercent={playerChangePercent}
          icon={ArrowTrendingUpIcon}
          color="text-slate-600"
        />

        <MetricCard
          title="Active Clubs"
          value={total_active_clubs}
          icon={CurrencyDollarIcon}
          color="text-purple-600"
        />

        <MetricCard
          title="Avg Revenue/Club"
          value={formatCurrency(average_revenue_per_club)}
          icon={ArrowTrendingUpIcon}
          color="text-orange-600"
        />
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Annual Recurring Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-900">
                {formatCurrency(annual_recurring_revenue)}
              </p>
              <p className="text-sm text-gray-600 mt-2">
                Projected annual revenue
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Growth Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <p
                className={`text-3xl font-bold ${
                  growth_rate >= 0 ? "text-green-600" : "text-red-600"
                }`}
              >
                {growth_rate > 0 ? "+" : ""}
                {growth_rate.toFixed(1)}%
              </p>
              <p className="text-sm text-gray-600 mt-2">
                Month-over-month growth
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Churn Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <p
                className={`text-3xl font-bold ${
                  churn_rate <= 5
                    ? "text-green-600"
                    : churn_rate <= 10
                      ? "text-yellow-600"
                      : "text-red-600"
                }`}
              >
                {churn_rate.toFixed(1)}%
              </p>
              <p className="text-sm text-gray-600 mt-2">Monthly churn rate</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Revenue Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h4 className="font-medium text-gray-900 mb-4">
                By Subscription Tier
              </h4>
              <div className="space-y-3">
                {overview.revenue_by_tier?.map((tier, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center"
                  >
                    <span className="text-sm text-gray-600">
                      {tier.plan_name}
                    </span>
                    <div className="text-right">
                      <span className="font-medium">
                        {formatCurrency(tier.revenue)}
                      </span>
                      <span className="text-xs text-gray-500 ml-2">
                        ({tier.club_count} clubs)
                      </span>
                    </div>
                  </div>
                )) || (
                  <p className="text-sm text-gray-500">
                    No tier data available
                  </p>
                )}
              </div>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 mb-4">Payment Status</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Paid</span>
                  <span className="font-medium text-green-600">
                    {formatCurrency(overview.paid_amount || 0)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Pending</span>
                  <span className="font-medium text-yellow-600">
                    {formatCurrency(overview.pending_amount || 0)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Overdue</span>
                  <span className="font-medium text-red-600">
                    {formatCurrency(overview.overdue_amount || 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BillingOverview;
